import { RecalProcessAndGenEntity } from "domain/entity/Recalculation/RecalProcessAndGenEntity";
import _ from "lodash";
import { MessageConstant } from "presentation/constant/MessageConstant";
import { RecalProcessAndGenConstant } from "presentation/constant/Recalculation/RecalProcessAndGenConstant";
import { WorkspaceConstant } from "presentation/constant/WorkSpaceConstant";
import { useRecalProcessAndGenVM } from "presentation/hook/Recalculation/useRecalProcessAndGenVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useANAInfoTracked } from "presentation/store/ANAInfo";
import { useRecalProcessAndGenMaintenanceTracked } from "presentation/store/Recalculation/RecalProcessAndGenMaintenanceProvider";
import { ConfirmDeleteModal } from "presentation/view/components/ConfirmDeleteModal";
import { HeaderTitle } from "presentation/view/components/HeaderWithBackButton/HeaderTitle";
import { OverflowMenuButton } from "presentation/view/components/OverflowMenuButton";
import { memo, useCallback, useMemo, useState } from "react";
import { HPHButton, IconButton, Loader } from "veronica-ui-component/dist/component/core";
import { Sidebarheader, StyledAction } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";

const RecalProcessAndGenTitleBar: React.FC = () => {
    const recalProcessAndGenVM = useRecalProcessAndGenVM();
    const RECAL_PROCESS_GEN_CONSTANT = RecalProcessAndGenConstant;
    const [recalProcessAndGenState] = useRecalProcessAndGenMaintenanceTracked();
    const messageBarVM = useMessageBarVM();
    const { selectRows, recalProcessAndGenList, isShowGeneratePanel, isShowSubmitPanel, isShowEditPanel, searchCriteria } = recalProcessAndGenState;
    const [isShowDelete, setIsShowDelete] = useState<boolean>(false);
    const [deletedRows, setDeletedRows] = useState<RecalProcessAndGenEntity[]>();
    const [isLoading, setIsLoading] = useState(false);
    const [anaInfoState] = useANAInfoTracked();
    const { allowUpdate } = anaInfoState;

    const isAssignSeqNoDisable = useCallback(() => {
        if (_.isEmpty(selectRows)) {
            return true;
        }
        const grayNoUse = selectRows.filter(ety => ety.groupNo && (ety.groupNo === 3 || ety.groupNo === 4));
        const stateSet = new Set<string | null>();
        const mergeDiffBalanceIndSet = new Set<string | null>();
        selectRows.forEach(ety => {
            if (ety) {
                stateSet.add(ety.dtlState ?? "");
                mergeDiffBalanceIndSet.add(ety.mergeDiffBalanceInd ?? "");
            } else {
                stateSet.add(null);
                mergeDiffBalanceIndSet.add(null);
            }
        });
        if (!stateSet.has(null) && !stateSet.has("I") && !stateSet.has("SG")) {
            if (!_.isEmpty(grayNoUse)) {
                return true;
            } else {
                if (!stateSet.has("E")
                    && (stateSet.has("R")
                        || stateSet.has("F"))
                    && mergeDiffBalanceIndSet.size === 1) {
                    return false;
                }
            }
        }
        return true;
    }, [selectRows])

    const isAssignOneOffRateDisable = useCallback(() => {
        if (_.isEmpty(selectRows)) {
            return true;
        }
        const grayNoUse = selectRows.filter(ety => ety.groupNo && (ety.groupNo === 3 || ety.groupNo === 4));
        const stateSet = new Set<string | null>();
        const rateTypeSet = new Set<string | null>();
        const chargeTypeSet = new Set<string | null>();
        selectRows.forEach(ety => {
            if (ety) {
                stateSet.add(ety.dtlState ?? "");
                rateTypeSet.add(ety.rateType ?? "");
                if (ety.chargeType) {
                    chargeTypeSet.add(ety.chargeType);
                } else {
                    chargeTypeSet.add(null);
                }
            } else {
                stateSet.add(null);
                rateTypeSet.add(null);
                chargeTypeSet.add(null);
            }
        });
        if (!stateSet.has(null) && !stateSet.has("I") && !stateSet.has("SG")) {
            if (!_.isEmpty(grayNoUse)) {
                return true;
            } else {
                if (rateTypeSet.size === 1 && rateTypeSet.has("ONEOFF")) {
                    return false;
                } else if (!stateSet.has("I") && !stateSet.has("T)")) {
                    return false;
                }
            }
        }
        if (!chargeTypeSet.has(null) && chargeTypeSet.has("EMP")) {
            return true;
        }
        return true;
    }, [selectRows])

    const handleDeleteClick = useCallback(async () => {
        if (!_.isEmpty(selectRows)) {
            const rows = selectRows.filter(ety => ety.dtlState === "I" || ety.dtlState === "SG");
            if (!_.isEmpty(rows)) {
                messageBarVM.showError("Just can delete state is 'NIL', 'R', 'F' or 'E'.");
                return;
            }
            setDeletedRows(selectRows);
            setIsShowDelete(true);
        } else {
            messageBarVM.showError("Please select a record.");
            return;
        }
    }, [messageBarVM, selectRows]);

    const handleSubmitBySelection = useCallback(async () => {
        if (!_.isEmpty(selectRows)) {
            const grayRows = selectRows.filter(ety => ety.groupNo && (ety.groupNo === 3 || ety.groupNo === 4));
            if (!_.isEmpty(grayRows)) {
                messageBarVM.showError("Gray: Manual credit charge or invoice detail which fully credit by others credit note.");
                return;
            }
            const rows = selectRows.filter(ety => ety.dtlState !== "I" && ety.submitInd !== "Y");
            if (!_.isEmpty(rows)) {
                setIsLoading(true);
                recalProcessAndGenVM.onSubmitClick(rows, false).then(data => {
                    setIsLoading(false);
                });
            } else {
                messageBarVM.showError("Just can submit state is 'NIL', 'R', 'F' or 'E'.");
                return;
            }
        } else {
            messageBarVM.showError("Please select a record.");
            return;
        }
    }, [messageBarVM, recalProcessAndGenVM, selectRows]);

    const handleSubmitCriteria = useCallback(async () => {
        if (!_.isEmpty(recalProcessAndGenList)) {
            const grayRows = recalProcessAndGenList.filter(ety => ety.groupNo && (ety.groupNo === 3 || ety.groupNo === 4));
            if (!_.isEmpty(grayRows)) {
                messageBarVM.showError("Gray: Manual credit charge or invoice detail which fully credit by others credit note.");
                return;
            }
            const rows = recalProcessAndGenList.filter(ety => ety.dtlState !== "I" && ety.submitInd !== "Y");
            if (!_.isEmpty(rows)) {
                setIsLoading(true);
                recalProcessAndGenVM.onSubmitClick(rows, true).then(data => {
                    setIsLoading(false);
                });
            } else {
                messageBarVM.showError("Just can submit state is 'NIL', 'R', 'F' or 'E'.");
                return;
            }
        } else {
            messageBarVM.showError("No record found.");
            return;
        }
    }, [messageBarVM, recalProcessAndGenList, recalProcessAndGenVM]);

    const handleGenerate = useCallback(async () => {
        setIsLoading(true);
        recalProcessAndGenVM.onGenerateClick().then(data => {
            setIsLoading(false);
        });
    }, [recalProcessAndGenVM]);

    const handleCancel = useCallback(async () => {
        setIsShowDelete(false);
        setDeletedRows([]);
    }, []);

    const handleDelete = useCallback(async () => {
        setIsLoading(true);
        setIsShowDelete(false);
        if (!deletedRows) return;
        await recalProcessAndGenVM.onDelete(deletedRows).then(async data => {
            if (data && data.success) {
                await recalProcessAndGenVM.onSearch(searchCriteria).then(data => {
                    setIsLoading(false);
                    setDeletedRows([]);
                    messageBarVM.showSuccess(MessageConstant.common.DELETED_DATA_SUCCESSFUL);
                })
            } else {
                setDeletedRows([]);
                setIsLoading(false);
                messageBarVM.showError(data.data ?? "Delete Failed.");
                return;
            }
        });
    }, [deletedRows, messageBarVM, recalProcessAndGenVM, searchCriteria]);

    const handleSearchClick = useCallback(async () => {
        recalProcessAndGenVM.onSearchClick();
    }, [recalProcessAndGenVM]);

    const handleAssignSeqNoClick = useCallback(async () => {
        if (!_.isEmpty(selectRows)) {
            const stateSet = new Set<string | null>();
            const mergeDiffBalanceIndSet = new Set<string | null>();
            selectRows.forEach(ety => {
                if (ety) {
                    stateSet.add(ety.dtlState ?? "");
                    mergeDiffBalanceIndSet.add(ety.mergeDiffBalanceInd ?? "");

                } else {
                    stateSet.add(null);
                    mergeDiffBalanceIndSet.add(null);
                }
            });
            if (!stateSet.has(null) && !stateSet.has("I") && !stateSet.has("SG")) {
                if ((!stateSet.has("E") && (stateSet.has("R") || stateSet.has("F"))) && mergeDiffBalanceIndSet.size === 1) {
                    recalProcessAndGenVM.onEditPanelClick(true);
                    return;
                }
            }
        } else {
            messageBarVM.showError("No record found.");
            return;
        }
    }, [messageBarVM, recalProcessAndGenVM, selectRows]);

    const handleAssignOneOffRateClick = useCallback(async () => {
        if (!_.isEmpty(selectRows)) {
            const stateSet = new Set<string | null>();
            const rateTypeSet = new Set<string | null>();
            const chargeTypeSet = new Set<string | null>();
            selectRows.forEach(ety => {
                if (ety) {
                    stateSet.add(ety.dtlState ?? "");
                    rateTypeSet.add(ety.rateType ?? "");
                    if (ety.chargeType) {
                        chargeTypeSet.add(ety.chargeType);
                    } else {
                        chargeTypeSet.add(null);
                    }

                } else {
                    stateSet.add(null);
                    rateTypeSet.add(null);
                    chargeTypeSet.add(null);
                }
            });
            if (!stateSet.has(null) && !stateSet.has("I") && !stateSet.has("SG")) {
                if (rateTypeSet.size === 1 && rateTypeSet.has("ONEOFF")) {
                    if (selectRows.every(ety => ety.rateType === "ONEOFF")) {
                        setIsLoading(true);
                        await recalProcessAndGenVM.onSaveClick(false, null, null, selectRows).then(async data => {
                            await recalProcessAndGenVM.onSearch(searchCriteria).then(data => {
                                setIsLoading(false);
                                messageBarVM.showSuccess("Save Successfully.");
                            })
                        });
                        return;
                    } else {
                        recalProcessAndGenVM.onEditPanelClick(false);
                        return;
                    }
                } else if (!stateSet.has("I") && !stateSet.has("T")) {
                    if (selectRows.every(ety => ety.rateType === "ONEOFF")) {
                        setIsLoading(true);
                        await recalProcessAndGenVM.onSaveClick(false, null, null, selectRows).then(async data => {
                            await recalProcessAndGenVM.onSearch(searchCriteria).then(data => {
                                setIsLoading(false);
                                messageBarVM.showSuccess("Save Successfully.");
                            })
                        });
                        return;
                    } else {
                        recalProcessAndGenVM.onEditPanelClick(false);
                        return;
                    }
                }
            }
        } else {
            messageBarVM.showError("No record found.");
            return;
        }
    }, [messageBarVM, recalProcessAndGenVM, searchCriteria, selectRows]);

    const isDisable = useCallback(() => {
        if (_.isEmpty(selectRows) || isShowGeneratePanel || isShowSubmitPanel || isShowEditPanel) {
            return true;
        }
        return false;
    }, [isShowEditPanel, isShowGeneratePanel, isShowSubmitPanel, selectRows]);

    const memoBulkEditMenu = useMemo(() => {
        return [
            {
                title: 'Assign Seq. No.',
                disabled: isAssignSeqNoDisable(),
                command: () => {
                    handleAssignSeqNoClick();
                }
            },
            {
                title: 'Assign/Resume New One-off Rate',
                disabled: isAssignOneOffRateDisable(),
                command: () => {
                    handleAssignOneOffRateClick();
                }
            },

        ]
    }, [handleAssignOneOffRateClick, handleAssignSeqNoClick, isAssignOneOffRateDisable, isAssignSeqNoDisable]);



    const memoMoreIconButton = useMemo(() => {
        let disabled: boolean = false;
        if (isDisable()) {
            disabled = true;
        }
        return <OverflowMenuButton buttonLabel={WorkspaceConstant.Common.BUTTON_MORE} disabled={disabled}
            toolTipText={WorkspaceConstant.Common.BUTTON_MORE} navigationList={memoBulkEditMenu} buttonSize={"Small"} buttonTheme={"Secondary"} />
    }, [isDisable, memoBulkEditMenu])

    return <Sidebarheader style={{ width: '100%', display: "flex", alignItems: "center" }}>
        {(isLoading) && <Loader Indicator="Spinner" size="Medium" />}
        <HeaderTitle>{RECAL_PROCESS_GEN_CONSTANT.TITLE}</HeaderTitle>
        <StyledAction className="tm-animated-wrapper">
            <IconButton fileName='Icon-search' disabled={isShowGeneratePanel || isShowSubmitPanel || isShowEditPanel} size='medium' toolTipText={'Search'} toolTipArrow={false} onClick={handleSearchClick} />
            {allowUpdate && <>
                {<HPHButton disabled={isDisable()} label={WorkspaceConstant.Common.BUTTON_DELETE} size={'Small'} theme={'Secondary'} onClick={handleDeleteClick} />}
                {<HPHButton disabled={isDisable()} label={WorkspaceConstant.Common.BUTTON_SUBMIT_RECAL_SELECTION} size={'Small'} theme={'Secondary'} onClick={handleSubmitBySelection} />}
                {<HPHButton disabled={isShowGeneratePanel || isShowSubmitPanel || isShowEditPanel} label={WorkspaceConstant.Common.BUTTON_SUBMIT_RECAL_CRITERIA} size={'Small'} theme={'Secondary'} onClick={handleSubmitCriteria} />}
                {<HPHButton disabled={isShowGeneratePanel || isShowSubmitPanel || isShowEditPanel} label={WorkspaceConstant.Common.BUTTON_GENGEATE} size={'Small'} theme={'Primary'} onClick={handleGenerate} />}
                {memoMoreIconButton}
            </>}
            {isShowDelete && <ConfirmDeleteModal headerText={`${WorkspaceConstant.Common.BUTTON_DELETE} ${RECAL_PROCESS_GEN_CONSTANT.TITLE}`}
                contentText={`${MessageConstant.common.DELETE_ALERT_TITLE}\n${MessageConstant.common.DELETE_ALERT_MESSAGE}`}
                visible={isShowDelete}
                onCancelClick={handleCancel}
                onDeleteClick={handleDelete}
            />}
        </StyledAction>
        {recalProcessAndGenState.forceRefresh && <></>}
    </Sidebarheader>
}

export default memo(RecalProcessAndGenTitleBar);